import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  IconButton,
  Tooltip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Visibility as ViewIcon,
  AccountBalanceWallet as WalletIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Pending as PendingIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { walletAPI } from '../../services/api';

const WithdrawalManagement = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    action: '',
    withdrawal: null,
  });
  const [detailsDialog, setDetailsDialog] = useState({
    open: false,
    withdrawal: null,
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchWithdrawals();
  }, []);

  const fetchWithdrawals = async () => {
    try {
      setLoading(true);
      const response = await walletAPI.getWithdrawals();
      if (response.data.success) {
        setWithdrawals(response.data.withdrawals);
      } else {
        toast.error('Failed to fetch withdrawals');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to fetch withdrawals');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (transactionId) => {
    try {
      setActionLoading(true);
      const response = await walletAPI.approveWithdrawal(transactionId);
      if (response.data.success) {
        toast.success('Withdrawal approved successfully');
        fetchWithdrawals();
        setConfirmDialog({ open: false, action: '', withdrawal: null });
      } else {
        toast.error('Failed to approve withdrawal');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to approve withdrawal');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (transactionId) => {
    try {
      setActionLoading(true);
      const response = await walletAPI.rejectWithdrawal(transactionId);
      if (response.data.success) {
        toast.success('Withdrawal rejected and amount reverted');
        fetchWithdrawals();
        setConfirmDialog({ open: false, action: '', withdrawal: null });
      } else {
        toast.error('Failed to reject withdrawal');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reject withdrawal');
    } finally {
      setActionLoading(false);
    }
  };

  const openConfirmDialog = (action, withdrawal) => {
    setConfirmDialog({ open: true, action, withdrawal });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ open: false, action: '', withdrawal: null });
  };

  const openDetailsDialog = (withdrawal) => {
    setDetailsDialog({ open: true, withdrawal });
  };

  const closeDetailsDialog = () => {
    setDetailsDialog({ open: false, withdrawal: null });
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      pending: { color: 'warning', icon: <PendingIcon />, label: 'Pending' },
      approved: { color: 'success', icon: <ApproveIcon />, label: 'Approved' },
      success: { color: 'success', icon: <ApproveIcon />, label: 'Success' },
      rejected: { color: 'error', icon: <RejectIcon />, label: 'Rejected' },
      failed: { color: 'error', icon: <RejectIcon />, label: 'Failed' },
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color}
        size="small"
        variant="outlined"
      />
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount).toFixed(2)}`;
  };

  const getStats = () => {
    const pending = withdrawals.filter(w => w.status === 'pending');
    const approved = withdrawals.filter(w => w.status === 'approved' || w.status === 'success');
    const rejected = withdrawals.filter(w => w.status === 'rejected' || w.status === 'failed');
    
    const totalPendingAmount = pending.reduce((sum, w) => sum + parseFloat(w.amount), 0);
    const totalApprovedAmount = approved.reduce((sum, w) => sum + parseFloat(w.amount), 0);

    return { pending, approved, rejected, totalPendingAmount, totalApprovedAmount };
  };

  const stats = getStats();

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedWithdrawals = withdrawals.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
        Withdrawal Management
      </Typography>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <motion.div whileHover={{ scale: 1.02 }}>
            <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Pending
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {stats.pending.length}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      {formatCurrency(stats.totalPendingAmount)}
                    </Typography>
                  </Box>
                  <PendingIcon sx={{ fontSize: 48, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <motion.div whileHover={{ scale: 1.02 }}>
            <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Approved
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {stats.approved.length}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      {formatCurrency(stats.totalApprovedAmount)}
                    </Typography>
                  </Box>
                  <TrendingUpIcon sx={{ fontSize: 48, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <motion.div whileHover={{ scale: 1.02 }}>
            <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Rejected
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {stats.rejected.length}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Funds Reverted
                    </Typography>
                  </Box>
                  <TrendingDownIcon sx={{ fontSize: 48, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <motion.div whileHover={{ scale: 1.02 }}>
            <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Total
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {withdrawals.length}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      All Requests
                    </Typography>
                  </Box>
                  <WalletIcon sx={{ fontSize: 48, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
      </Grid>

      {/* Withdrawals Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Withdrawal Requests
          </Typography>
          
          <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>User Details</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Payment Info</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Date</TableCell>
                  {!isMobile && <TableCell>Approved By</TableCell>}
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedWithdrawals.map((withdrawal) => (
                  <TableRow key={withdrawal.id} hover>
                    <TableCell>{withdrawal.id}</TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {withdrawal.fromUser?.username || withdrawal.from_user_id}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          ID: {withdrawal.from_user_id}
                        </Typography>
                        {withdrawal.fromUser?.user_type && (
                          <>
                            <br />
                            <Chip 
                              label={withdrawal.fromUser.user_type} 
                              size="small" 
                              variant="outlined"
                              sx={{ mt: 0.5, height: '20px', fontSize: '0.7rem' }}
                            />
                          </>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        {formatCurrency(withdrawal.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        {withdrawal.payment_method && (
                          <Typography variant="body2" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                            {withdrawal.payment_method}
                          </Typography>
                        )}
                        {withdrawal.reference_id && (
                          <Typography variant="caption" color="textSecondary">
                            {withdrawal.reference_id}
                          </Typography>
                        )}
                        {withdrawal.bank_info && (
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                              {withdrawal.bank_info.account_holder_name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                              A/C: {withdrawal.bank_info.account_number}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                              {withdrawal.bank_info.bank_name} ({withdrawal.bank_info.ifsc_code})
                            </Typography>
                          </Box>
                        )}
                        {!withdrawal.payment_method && !withdrawal.reference_id && !withdrawal.bank_info && (
                          <Typography variant="caption" color="textSecondary">
                            -
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{getStatusChip(withdrawal.status)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(withdrawal.created_at)}
                      </Typography>
                    </TableCell>
                    {!isMobile && (
                      <TableCell>
                        {withdrawal.approver ? (
                          <Typography variant="body2">
                            {withdrawal.approver.username}
                            <br />
                            <Typography variant="caption" color="textSecondary">
                              {/* ID: {withdrawal.approver.id} */}
                            </Typography>
                            {withdrawal.approved_at && (
                              <>
                                <br />
                                <Typography variant="caption" color="textSecondary">
                                  {formatDate(withdrawal.approved_at)}
                                </Typography>
                              </>
                            )}
                          </Typography>
                        ) : withdrawal.approved_by ? (
                          <Typography variant="body2">
                            Admin #{withdrawal.approved_by}
                            {withdrawal.approved_at && (
                              <>
                                <br />
                                <Typography variant="caption" color="textSecondary">
                                  {formatDate(withdrawal.approved_at)}
                                </Typography>
                              </>
                            )}
                          </Typography>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    )}
                    <TableCell align="center">
                      <Box display="flex" gap={1} justifyContent="center">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => openDetailsDialog(withdrawal)}
                            color="info"
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        
                        {(user.user_type === 'admin' || user.user_type === 'owner') && 
                         withdrawal.status === 'pending' && (
                          <>
                            <Tooltip title="Approve">
                              <IconButton
                                size="small"
                                onClick={() => openConfirmDialog('approve', withdrawal)}
                                color="success"
                                disabled={actionLoading}
                              >
                                <ApproveIcon />
                              </IconButton>
                            </Tooltip>
                            
                            <Tooltip title="Reject">
                              <IconButton
                                size="small"
                                onClick={() => openConfirmDialog('reject', withdrawal)}
                                color="error"
                                disabled={actionLoading}
                              >
                                <RejectIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={withdrawals.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={closeConfirmDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Confirm {confirmDialog.action === 'approve' ? 'Approval' : 'Rejection'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to {confirmDialog.action} withdrawal request of{' '}
            <strong>{formatCurrency(confirmDialog.withdrawal?.amount || 0)}</strong> for user{' '}
            <strong>{confirmDialog.withdrawal?.from_user_id}</strong>?
            {confirmDialog.action === 'reject' && (
              <Typography color="warning.main" sx={{ mt: 1 }}>
                Note: The amount will be reverted to the user's wallet.
              </Typography>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDialog} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            onClick={() =>
              confirmDialog.action === 'approve'
                ? handleApprove(confirmDialog.withdrawal.id)
                : handleReject(confirmDialog.withdrawal.id)
            }
            variant="contained"
            color={confirmDialog.action === 'approve' ? 'success' : 'error'}
            disabled={actionLoading}
            startIcon={actionLoading && <CircularProgress size={20} />}
          >
            {confirmDialog.action === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialog.open}
        onClose={closeDetailsDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Withdrawal Details</DialogTitle>
        <DialogContent>
          {detailsDialog.withdrawal && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Transaction ID
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {detailsDialog.withdrawal.id}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  User Details
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  {detailsDialog.withdrawal.fromUser?.username || 'N/A'}
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block' }}>
                  User ID: {detailsDialog.withdrawal.from_user_id}
                </Typography>
                {detailsDialog.withdrawal.fromUser?.user_type && (
                  <Chip 
                    label={detailsDialog.withdrawal.fromUser.user_type} 
                    size="small" 
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />
                )}
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Amount
                </Typography>
                <Typography variant="h6" color="primary.main" sx={{ mb: 2 }}>
                  {formatCurrency(detailsDialog.withdrawal.amount)}
                </Typography>
              </Grid>

              {detailsDialog.withdrawal.payment_method && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Payment Method
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2, textTransform: 'uppercase' }}>
                    {detailsDialog.withdrawal.payment_method}
                  </Typography>
                </Grid>
              )}

              {detailsDialog.withdrawal.reference_id && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Reference ID / UPI
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {detailsDialog.withdrawal.reference_id}
                  </Typography>
                </Grid>
              )}

              {detailsDialog.withdrawal.bank_info && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Bank Information
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body1">
                      <strong>Account Holder:</strong> {detailsDialog.withdrawal.bank_info.account_holder_name}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Account Number:</strong> {detailsDialog.withdrawal.bank_info.account_number}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Bank Name:</strong> {detailsDialog.withdrawal.bank_info.bank_name}
                    </Typography>
                    <Typography variant="body1">
                      <strong>IFSC Code:</strong> {detailsDialog.withdrawal.bank_info.ifsc_code}
                    </Typography>
                  </Box>
                </Grid>
              )}

              {detailsDialog.withdrawal.notes && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Notes
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {detailsDialog.withdrawal.notes}
                  </Typography>
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Status
                </Typography>
                <Box sx={{ mb: 2 }}>
                  {getStatusChip(detailsDialog.withdrawal.status)}
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Request Date
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {formatDate(detailsDialog.withdrawal.created_at)}
                </Typography>
              </Grid>

              {(detailsDialog.withdrawal.approver || detailsDialog.withdrawal.approved_by) && (
                <>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Approved/Rejected By
                    </Typography>
                    {detailsDialog.withdrawal.approver ? (
                      <>
                        <Typography variant="body1" sx={{ mb: 0.5 }}>
                          {detailsDialog.withdrawal.approver.username}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block' }}>
                          Admin ID: {detailsDialog.withdrawal.approver.id}
                        </Typography>
                        <Chip 
                          label={detailsDialog.withdrawal.approver.user_type} 
                          size="small" 
                          variant="outlined"
                          sx={{ mb: 2 }}
                        />
                      </>
                    ) : (
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        Admin #{detailsDialog.withdrawal.approved_by}
                      </Typography>
                    )}
                  </Grid>

                  {detailsDialog.withdrawal.approved_at && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Action Date
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        {formatDate(detailsDialog.withdrawal.approved_at)}
                      </Typography>
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetailsDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
};

export default WithdrawalManagement;
